import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { useSelector } from 'store';
import { Register } from 'store/accounts/type';
import { JoinInfo } from 'store/accounts/state';
import AppleLogin from 'react-apple-login';

import {
  RequiredMessage,
  AgreeMessage,
  PASSWORD_VALIDATE_MSG,
  PASSWORD_UNMATCH_MSG,
  EMAIL_UNMATCH_MSG,
  USERNAME_OVER_MSG,
  PASSWORD_SAME_USERNAME_MSG,
} from 'constants/message';
import { isEmail } from 'constants/reg';

import { PostRegisterAPI, GetMyAccountAPI, PostEmailVerificationRequestAPI, PostEmailVerificationVerifyAPI } from 'api/accounts';
import { AxiosResponse } from 'axios';

import Modal from 'components/common/Modal';
import ModalTerms from 'components/Popup/ModalTerms';
import {
  getAPIErrorMessage,
  makeAuthorizationConfig,
  setLocalStitchCookie,
} from 'utils';
import { accountActions } from 'store/accounts';
import { useCookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import { GetTermsAPI } from 'api/site';
import { siteActions } from 'store/site';

const BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.LOCALSTITCH_BASE_URL
    : 'http://localhost:3000';
const REDIRECT_URL = `${BASE_URL}/auth/sns`;

const Join: React.FC = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { openModal, ModalPortal, closeModal } = Modal();
  const info = useSelector(state => state.accounts.join_info) || ({} as JoinInfo);
  const invitation = useSelector(state => state.membership.invite_code);
  const socialAccount = useSelector(state => state.accounts.socialAccountData);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [totalCh, setTotalCh] = useState(false);
  const [ag1, setAG1] = useState(false); // [필수] 이용약관 동의
  const [ag3, setAG3] = useState(false); // [선택] 마케팅 목적의 개인정보 수집·이용 동의
  const [ag4, setAG4] = useState(false); // [선택] 광고성 정보 수신 동의
  const [, setCookie] = useCookies();

  // 이메일 인증 관련 state
  const [verificationCode, setVerificationCode] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeToken, setCodeToken] = useState('');

  const appleLoginButtonRef = useRef<HTMLDivElement>();

  const onChangeUserName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
  };

  const onChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const onChangePassword2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword2(event.target.value);
  };

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    // 이메일 변경 시 인증 상태 초기화
    setIsEmailVerified(false);
    setIsCodeSent(false);
    setVerificationCode('');
    setCodeToken('');
  };

  const onChangeVerificationCode = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(event.target.value);
  };

  // 인증코드 발송
  const clickSendVerificationCode = async () => {
    const targetEmail = email;

    if (!targetEmail) {
      alert('이메일을 입력해주세요.');
      return;
    }

    if (!isEmail(targetEmail)) {
      alert(EMAIL_UNMATCH_MSG);
      return;
    }

    setIsSendingCode(true);

    try {
      const response: AxiosResponse = await PostEmailVerificationRequestAPI({ email: targetEmail });

      if (response.status >= 400) {
        const message = getAPIErrorMessage(response);
        alert(message);
        setIsSendingCode(false);
        return;
      }

      setCodeToken(response.data.code_token);
      setIsCodeSent(true);
      alert('인증코드가 발송되었습니다. 이메일을 확인해주세요.');
    } catch (error) {
      alert('인증코드 발송에 실패했습니다. 다시 시도해주세요.');
    }

    setIsSendingCode(false);
  };

  // 인증코드 확인
  const clickVerifyCode = async () => {
    if (!verificationCode) {
      alert('인증코드를 입력해주세요.');
      return;
    }

    setIsVerifying(true);

    try {
      const response: AxiosResponse = await PostEmailVerificationVerifyAPI({
        code_token: codeToken,
        code: verificationCode
      });

      if (response.status >= 400) {
        const message = getAPIErrorMessage(response);
        alert(message);
        setIsVerifying(false);
        return;
      }

      setIsEmailVerified(true);
      alert('이메일 인증이 완료되었습니다.');
    } catch (error) {
      alert('인증코드 확인에 실패했습니다. 다시 시도해주세요.');
    }

    setIsVerifying(false);
  };

  const onChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onChangePhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value);
  };

  const isPasswordNotRequired =
    info.socialaccounts?.length > 0 && info.socialaccounts[0] === 'APPLE';

  const onChangeTotalRequired = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTotalCh(event.target.checked);
    setAG1(event.target.checked);
    setAG3(event.target.checked);
    setAG4(event.target.checked);
  };

  const onChangeAg1 = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAG1(event.target.checked);

    if (event.target.checked && ag3 && ag4) setTotalCh(true);
    else setTotalCh(false);
  };

  const onChangeAg3 = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAG3(event.target.checked);

    if (ag1 && event.target.checked && ag4) setTotalCh(true);
    else setTotalCh(false);
  };

  const onChangeAg4 = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAG4(event.target.checked);

    if (ag1 && ag3 && event.target.checked) setTotalCh(true);
    else setTotalCh(false);
  };

  const modalOpen = async (type: string) => {
    const { data } = await GetTermsAPI(type);
    dispatch(siteActions.getTerm(data));

    openModal();
  };

  const clickSubmit = async () => {
    if (!validate()) return;

    const register: Register = {
      username,
      email: existSocialMail
        ? socialAccount.email_addresses[0].email
        : invitation?.email
        ? invitation?.email
        : info.email || email,
      password1: isPasswordNotRequired ? undefined : password,
      password2: isPasswordNotRequired ? undefined : password2,
      real_name: name || info.name,
      phone_number: phoneNumber || info.phone_number,
      id_verification_tid: info.uid || undefined,
      is_agreed_marketing: ag3,
      is_agreed_advertisement: ag4,
      social_account: socialAccount ? socialAccount.social_account : undefined,
      email_addresses: socialAccount
        ? socialAccount.email_addresses
        : undefined,
    };

    const response: AxiosResponse = await PostRegisterAPI(register);

    if (response.status >= 400) {
      alert(getAPIErrorMessage(response));
      return;
    }
    const config = makeAuthorizationConfig(response.data.access_token);
    setLocalStitchCookie(
      setCookie,
      response.data.access_token,
      response.data.refresh_token,
    );

    dispatch(accountActions.setLogin(true));
    dispatch(accountActions.setAccessToken(response.data.access_token));

    const user: AxiosResponse = await GetMyAccountAPI(config);

    if (user.status >= 400) {
      alert(getAPIErrorMessage(user));
      return;
    }

    dispatch(accountActions.setUserInfo(user.data));

    invitationRoute();
  };

  const invitationRoute = () => {
    if (invitation !== null) {
      router.push('/membership/invite_complete');
    } else {
      router.push('/');
    }
  };

  const validate = () => {
    if (!username.length) {
      alert(RequiredMessage('아이디'));
      return false;
    }

    if (username.length >= 16) {
      alert(USERNAME_OVER_MSG);
      return false;
    }

    if (!password.length && !isPasswordNotRequired) {
      alert(RequiredMessage('비밀번호'));
      return false;
    }

    if (!password2.length && !isPasswordNotRequired) {
      alert(RequiredMessage('비밀번호 확인'));
      return false;
    }

    const finalEmail = existSocialMail
      ? socialAccount.email_addresses[0].email
      : invitation?.email
      ? invitation?.email
      : info.email || email;

    if (!finalEmail) {
      alert(RequiredMessage('이메일'));
      return false;
    }

    if (!name && !info.name) {
      alert(RequiredMessage('이름'));
      return false;
    }

    if (!phoneNumber && !info.phone_number) {
      alert(RequiredMessage('휴대전화 번호'));
      return false;
    }

    if (password.length < 10 && !isPasswordNotRequired) {
      alert(PASSWORD_VALIDATE_MSG);
      return false;
    }

    if (password !== password2) {
      alert(PASSWORD_UNMATCH_MSG);
      return false;
    }

    if (!existSocialMail && !invitation?.email && !info.email && !isEmail(email)) {
      alert(EMAIL_UNMATCH_MSG);
      return false;
    }

    // 이메일 인증 체크 (소셜 로그인, 초대 이메일, 본인인증 이메일이 아닌 경우에만)
    if (!existSocialMail && !invitation?.email && !info.email && !isEmailVerified) {
      alert('이메일 인증을 완료해주세요.');
      return false;
    }

    if (!ag1) {
      alert(AgreeMessage('이용약관'));
      return false;
    }

    if (password.includes(username)) {
      alert(PASSWORD_SAME_USERNAME_MSG);
      return false;
    }

    return true;
  };

  const existSocialMail =
    socialAccount !== undefined && socialAccount?.email_addresses?.length > 0;

  return (
    <>
      <div className="mem-wrapper">
        <h2>회원가입</h2>
        <div className="join-input">
          <fieldset>
            <legend>회원가입</legend>
            <ul>
              <li>
                <label htmlFor="id">
                  아이디{' '}
                  <i className="ness">
                    <span>필수</span>
                  </i>
                </label>
                <input
                  type="text"
                  id="id"
                  placeholder="아이디를 입력하세요"
                  value={username}
                  onChange={onChangeUserName}
                />
              </li>
              {!isPasswordNotRequired && (
                <>
                  <li>
                    <label htmlFor="pw">
                      비밀번호{' '}
                      <i className="ness">
                        <span>필수</span>
                      </i>
                    </label>
                    <input
                      type="password"
                      id="pw"
                      placeholder="영문,숫자,특수기호 10자 이상"
                      value={password}
                      onChange={onChangePassword}
                    />
                  </li>
                  <li>
                    <label htmlFor="pw2">
                      비밀번호 확인{' '}
                      <i className="ness">
                        <span>필수</span>
                      </i>
                    </label>
                    <input
                      type="password"
                      id="pw2"
                      placeholder="영문,숫자,특수기호 10자 이상"
                      value={password2}
                      onChange={onChangePassword2}
                    />
                  </li>
                </>
              )}
              <li>
                <label htmlFor="eMail">
                  {invitation?.email ? '초대받은 이메일 ' : '이메일 '}
                  <i className="ness">
                    <span>필수</span>
                  </i>
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    id="eMail"
                    placeholder="@ 포함된 이메일 주소 입력"
                    style={{ flex: 1 }}
                    value={
                      existSocialMail
                        ? socialAccount.email_addresses[0].email
                        : invitation?.email
                        ? invitation?.email
                        : info.email || email
                    }
                    disabled={existSocialMail || Boolean(invitation?.email) || Boolean(info.email) || isEmailVerified}
                    onChange={
                      existSocialMail
                        ? () => setEmail(socialAccount.email_addresses[0].email)
                        : onChangeEmail
                    }
                  />
                  {!existSocialMail && !invitation?.email && !info.email && (
                    <button
                      type="button"
                      onClick={clickSendVerificationCode}
                      disabled={isSendingCode || isEmailVerified}
                      style={{
                        minWidth: '100px',
                        height: '44px',
                        border: '1px solid #111',
                        background: isEmailVerified ? '#f0f0f0' : '#fff',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        cursor: isEmailVerified ? 'default' : 'pointer'
                      }}
                    >
                      {isSendingCode ? '발송중...' : isCodeSent ? '재발송' : '인증코드 발송'}
                    </button>
                  )}
                </div>
                {isEmailVerified && (
                  <p style={{ color: '#0066cc', fontSize: '12px', marginTop: '5px' }}>인증 완료</p>
                )}
              </li>
              {/* 인증코드 입력 필드 */}
              {isCodeSent && !isEmailVerified && !existSocialMail && !invitation?.email && !info.email && (
                <li>
                  <label htmlFor="verificationCode">
                    인증코드{' '}
                    <i className="ness">
                      <span>필수</span>
                    </i>
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      id="verificationCode"
                      placeholder="이메일로 받은 인증코드 입력"
                      style={{ flex: 1 }}
                      value={verificationCode}
                      onChange={onChangeVerificationCode}
                    />
                    <button
                      type="button"
                      onClick={clickVerifyCode}
                      disabled={isVerifying}
                      style={{
                        minWidth: '100px',
                        height: '44px',
                        border: '1px solid #111',
                        background: '#111',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      {isVerifying ? '확인중...' : '인증 확인'}
                    </button>
                  </div>
                </li>
              )}
              <li>
                <label htmlFor="name">
                  이름{' '}
                  <i className="ness">
                    <span>필수</span>
                  </i>
                </label>
                <input
                  type="text"
                  id="name"
                  placeholder="이름을 입력하세요"
                  value={name || info.name}
                  onChange={onChangeName}
                  disabled={Boolean(info.name)}
                />
              </li>
              <li>
                <label htmlFor="cTell">
                  휴대전화 번호{' '}
                  <i className="ness">
                    <span>필수</span>
                  </i>
                </label>
                <input
                  type="text"
                  id="cTell"
                  placeholder="휴대전화 번호를 입력하세요"
                  value={phoneNumber || info.phone_number}
                  onChange={onChangePhoneNumber}
                  disabled={Boolean(info.phone_number)}
                />
              </li>
            </ul>
            <div className="agree-box">
              <p>
                <input
                  type="checkbox"
                  id="totalCh"
                  checked={totalCh}
                  onChange={onChangeTotalRequired}
                />
                <label htmlFor="totalCh">모두 동의합니다.</label>
              </p>
              <ul>
                <li>
                  <input
                    type="checkbox"
                    id="ag1"
                    checked={ag1}
                    onChange={onChangeAg1}
                  />
                  <label htmlFor="ag1">[필수] 이용약관 동의</label>
                  <a onClick={() => modalOpen('terms')}>보기</a>
                </li>
                <li>
                  <label>[필수 안내] 회원가입 개인정보 수집·이용 안내</label>
                  <a onClick={() => modalOpen('privacy')}>보기</a>
                </li>
                <li>
                  <input
                    type="checkbox"
                    id="ag3"
                    checked={ag3}
                    onChange={onChangeAg3}
                  />
                  <label htmlFor="ag3">
                    [선택] 마케팅 목적의 개인정보 수집·이용 동의
                  </label>
                  <a onClick={() => modalOpen('marketing')}>보기</a>
                </li>
                <li>
                  <input
                    type="checkbox"
                    id="ag4"
                    checked={ag4}
                    onChange={onChangeAg4}
                  />
                  <label htmlFor="ag4">[선택] 광고성 정보 수신 동의</label>
                  <a onClick={() => modalOpen('advertisement')}>보기</a>
                </li>
              </ul>
            </div>
            <div className="btn">
              <a onClick={clickSubmit}>로컬스티치 가입하기 </a>
            </div>
          </fieldset>
        </div>
      </div>
      <AppleLogin
        clientId="com.localstitchs.LOCALSTITCHS"
        redirectURI={REDIRECT_URL}
        responseMode="query"
        responseType="code"
        state="APPLE_CONNECT"
        render={renderProps => (
          <div
            style={{ width: 0, height: 0 }}
            onClick={renderProps.onClick}
            ref={appleLoginButtonRef}
          />
        )}
      />
      <ModalPortal>
        <ModalTerms closeModal={closeModal} />
      </ModalPortal>
    </>
  );
};

export default Join;
